import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDate, fmtTime } from "@/utils/date";
import PhoneInput from "@/components/PhoneInput";
import MultiPhoneInput, { hydratePhones, serializePhones, validatePhonesList } from "@/components/MultiPhoneInput";
import {
  Crown,
  Plus,
  PencilSimple,
  Trash,
  Key,
  X,
  Copy,
  Warning,
  ShieldCheck,
  Phone,
  WhatsappLogo,
} from "@phosphor-icons/react";

const ADMIN_TYPE_LABEL = {
  general_manager: "المدير العام",
  booking_manager: "مدير الحجوزات",
  manager: "مدير",
};

function formatDT(iso) {
  if (!iso) return "—";
  const d = fmtDate(iso);
  const t = fmtTime(iso);
  return `${d} · ${t}`;
}

/* ─────────────────────────  Admin Modal (Create / Edit)  ───────────────────────── */
function AdminModal({ open, admin, onClose, onSaved }) {
  const isEdit = !!admin;
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phones, setPhones] = useState([{ number: "+966", is_primary: true }]);
  const [adminType, setAdminType] = useState("manager");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFullName(admin?.full_name || "");
    setEmail(admin?.email || "");
    setPhones(hydratePhones(admin?.phones, admin?.phone));
    setAdminType(admin?.admin_type || "manager");
  }, [open, admin]);

  if (!open) return null;

  const handleSave = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) return toast.error("أدخل الاسم الكامل");
    if (!isEdit && !email.trim()) return toast.error("أدخل البريد الإلكتروني");
    const phoneError = validatePhonesList(phones, { requireAtLeastOne: false });
    if (phoneError) return toast.error(phoneError);
    const cleanedPhones = serializePhones(phones);
    setBusy(true);
    try {
      if (isEdit) {
        const { data } = await api.patch(`/superadmin/admins/${admin.id}`, {
          full_name: fullName.trim(),
          phones: cleanedPhones,
          admin_type: adminType,
        });
        toast.success("تم حفظ التعديلات");
        onSaved(data, null);
      } else {
        const { data } = await api.post("/superadmin/admins", {
          full_name: fullName.trim(),
          email: email.trim().toLowerCase(),
          phones: cleanedPhones,
          admin_type: adminType,
        });
        toast.success("تم إنشاء الأدمن");
        onSaved(data.admin, data.password);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحفظ");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-team-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSave}
        className="w-full max-w-lg bg-[#0F1F42] border border-primary/30 rounded-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between p-4 border-b border-primary/20">
          <h3 className="text-white font-bold flex items-center gap-2">
            <Crown size={18} weight="duotone" className="text-primary" />
            {isEdit ? "تعديل الأدمن" : "إضافة أدمن جديد"}
          </h3>
          <button type="button" onClick={onClose} className="text-white/60 hover:text-white">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="block text-xs text-white/60 mb-1">الاسم الكامل *</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="mz-input w-full"
              placeholder="اسم الأدمن"
              data-testid="admin-team-name"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">البريد الإلكتروني *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mz-input w-full ltr-input"
              dir="ltr"
              placeholder="admin@example.com"
              disabled={isEdit}
              data-testid="admin-team-email"
              required={!isEdit}
            />
            {isEdit && (
              <p className="text-[10px] text-white/40 mt-1">لا يمكن تعديل البريد الإلكتروني</p>
            )}
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">أرقام التواصل</label>
            <MultiPhoneInput
              value={phones}
              onChange={setPhones}
              testIdPrefix="admin-team-phones"
              maxEntries={5}
            />
          </div>
          <div>
            <label className="block text-xs text-white/60 mb-1">نوع الصلاحية</label>
            <div className="flex flex-wrap gap-2" data-testid="admin-team-type-chips">
              {Object.entries(ADMIN_TYPE_LABEL).map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => setAdminType(key)}
                  data-testid={`admin-team-type-${key}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                    adminType === key
                      ? "bg-primary text-primary-foreground border-primary font-bold"
                      : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-primary/20 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-white/20 text-white/80 hover:bg-white/5 text-sm"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-team-save"
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:brightness-110 disabled:opacity-50"
          >
            {busy ? "جارٍ الحفظ…" : isEdit ? "حفظ التعديلات" : "إنشاء الأدمن"}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─────────────────────────  Password Reveal Modal  ───────────────────────── */
function PasswordRevealModal({ password, email, onClose }) {
  if (!password) return null;

  const copy = () => {
    navigator.clipboard.writeText(password);
    toast.success("تم نسخ كلمة السر");
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-team-password-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-[#0F1F42] border-2 border-[#F57F17]/60 rounded-2xl overflow-hidden"
      >
        <div className="p-5 border-b border-[#F57F17]/30 flex items-center gap-3 bg-[#F57F17]/10">
          <Warning size={24} weight="duotone" className="text-[#F57F17]" />
          <div>
            <h3 className="text-[#F57F17] font-bold">كلمة السر الجديدة</h3>
            <p className="text-white/70 text-xs mt-0.5">احفظها الآن — لن تُعرض مرة أخرى</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {email && (
            <div>
              <div className="text-white/50 text-xs mb-1">البريد الإلكتروني</div>
              <div className="ltr-input text-white bg-white/5 px-3 py-2 rounded-lg text-sm num" dir="ltr">
                {email}
              </div>
            </div>
          )}
          <div>
            <div className="text-white/50 text-xs mb-1">كلمة السر</div>
            <div className="flex items-center gap-2">
              <div
                className="flex-1 ltr-input font-mono text-[#4ADE80] bg-black/30 px-3 py-3 rounded-lg text-base border border-[#4ADE80]/30 select-all"
                dir="ltr"
                data-testid="admin-team-password-value"
              >
                {password}
              </div>
              <button
                type="button"
                onClick={copy}
                data-testid="admin-team-password-copy"
                className="px-3 py-3 rounded-lg bg-primary/20 hover:bg-primary/30 border border-primary/50 text-primary flex items-center gap-1 text-sm"
              >
                <Copy size={16} /> نسخ
              </button>
            </div>
          </div>
          <div className="text-[11px] text-white/50 bg-white/5 rounded-lg p-3 leading-relaxed">
            • أرسل كلمة السر للأدمن عبر قناة آمنة (WhatsApp/SMS شخصياً).
            <br />• كلمة السر عشوائية 12 حرف تحوي حروف صغيرة وكبيرة وأرقاماً ورمزاً خاصاً.
            <br />• لن يستطيع أحد استرجاع كلمة السر بعد إغلاق هذه النافذة.
          </div>
        </div>
        <div className="p-4 border-t border-primary/20 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-team-password-close"
            className="px-4 py-2 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:brightness-110"
          >
            تم — أغلق
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────  Main Page  ───────────────────────── */
export default function AdminTeam() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const confirm = useConfirm();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [revealPassword, setRevealPassword] = useState(null); // { password, email }

  useEffect(() => {
    if (authLoading) return;
    if (!user?.is_super_admin) {
      toast.error("غير مصرح — هذه الصفحة للمدير العام فقط");
      navigate("/admin", { replace: true });
    }
  }, [user, authLoading, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/admins");
      setAdmins(data);
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("غير مصرح");
        navigate("/admin", { replace: true });
      } else {
        toast.error("تعذر جلب قائمة الأدمن");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_super_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_super_admin]);

  const handleCreate = () => {
    setEditingAdmin(null);
    setModalOpen(true);
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setModalOpen(true);
  };

  const handleSaved = (adminDoc, plainPassword) => {
    setModalOpen(false);
    setEditingAdmin(null);
    if (plainPassword) {
      setRevealPassword({ password: plainPassword, email: adminDoc?.email });
    }
    load();
  };

  const handleResetPassword = async (admin) => {
    const ok = await confirm({
      title: "إعادة تعيين كلمة السر",
      message: `سيتم توليد كلمة سر جديدة عشوائية للأدمن ${admin.full_name || admin.email}. هل أنت متأكد؟`,
      confirmText: "توليد كلمة سر جديدة",
      tone: "warning",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/superadmin/admins/${admin.id}/reset-password`);
      setRevealPassword({ password: data.password, email: data.email });
      toast.success("تم توليد كلمة السر");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التوليد");
    }
  };

  const handleDelete = async (admin) => {
    if (admin.is_super_admin) return;
    const ok = await confirm({
      title: "حذف الأدمن",
      message: `سيتم حذف الأدمن ${admin.full_name || admin.email} نهائياً. هل أنت متأكد؟`,
      confirmText: "حذف نهائي",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/superadmin/admins/${admin.id}`);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر الحذف");
    }
  };

  const rows = useMemo(() => admins || [], [admins]);

  if (authLoading || !user?.is_super_admin) {
    return (
      <div className="min-h-screen bg-background text-white flex items-center justify-center" dir="rtl">
        <div className="text-primary animate-pulse">جارٍ التحقق…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="px-4 py-6" data-testid="admin-team-page">
        <div className="max-w-5xl mx-auto space-y-5">
          {/* Header */}
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center">
                <Crown size={24} weight="duotone" className="text-primary" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">إدارة الأدمن</h1>
                <p className="text-white/50 text-sm mt-0.5 flex items-center gap-1.5">
                  <ShieldCheck size={12} weight="duotone" className="text-primary" />
                  للمدير العام فقط — {rows.length} حساب
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCreate}
              data-testid="admin-team-add-btn"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground font-bold text-sm hover:brightness-110"
            >
              <Plus size={18} weight="bold" /> إضافة أدمن جديد
            </button>
          </div>

          {/* Table */}
          <div className="bg-card border border-primary/15 rounded-2xl overflow-hidden">
            {loading ? (
              <div className="p-16 text-center text-primary animate-pulse">جارٍ التحميل…</div>
            ) : rows.length === 0 ? (
              <div className="p-16 text-center text-white/50">لا يوجد أدمن</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-sm" data-testid="admin-team-table">
                  <thead className="bg-[#0B1A3A] text-primary/80 text-xs">
                    <tr>
                      <th className="p-3 text-start">الاسم</th>
                      <th className="p-3 text-start">البريد الإلكتروني</th>
                      <th className="p-3 text-start">الجوال</th>
                      <th className="p-3 text-start whitespace-nowrap">الصلاحية</th>
                      <th className="p-3 text-start whitespace-nowrap">تاريخ الإنشاء</th>
                      <th className="p-3 text-start whitespace-nowrap">آخر دخول</th>
                      <th className="p-3 text-start">إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((a) => (
                      <tr
                        key={a.id}
                        data-testid={`admin-team-row-${a.id}`}
                        className="border-t border-primary/10 hover:bg-primary/5"
                      >
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="text-white font-semibold">{a.full_name || "—"}</div>
                            {a.is_super_admin && (
                              <span
                                data-testid={`admin-team-super-badge-${a.id}`}
                                className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-primary/20 border border-primary/50 text-primary font-bold"
                              >
                                <Crown size={10} weight="fill" /> Super
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 num text-white/80" dir="ltr">
                          {a.email}
                        </td>
                        <td className="p-3 text-white/70">
                          {(a.phones && a.phones.length > 0
                            ? a.phones
                            : a.phone ? [{ number: a.phone, is_primary: true }] : []
                          ).length === 0 ? (
                            <span className="text-white/40">—</span>
                          ) : (
                            <div className="space-y-1">
                              {(a.phones && a.phones.length > 0
                                ? a.phones
                                : [{ number: a.phone, is_primary: true }]
                              ).map((p, pIdx) => {
                                const digits = String(p.number || "").replace(/\D+/g, "");
                                if (!digits) return null;
                                return (
                                  <div key={pIdx} className="inline-flex items-center gap-1.5 flex-wrap" dir="ltr">
                                    <span className="text-white/85 num text-xs">{p.number}</span>
                                    {p.is_primary && (a.phones?.length > 1) && (
                                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/20 border border-primary/40 text-primary font-bold">★</span>
                                    )}
                                    <a href={`tel:${p.number}`} title="اتصال" className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary/15 border border-primary/40 text-primary hover:bg-primary/25">
                                      <Phone size={10} weight="fill" />
                                    </a>
                                    <a href={`https://wa.me/${digits}`} target="_blank" rel="noopener noreferrer" title="واتساب" className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#25D366] hover:bg-[#20BA5A]">
                                      <WhatsappLogo size={10} weight="fill" className="text-white" />
                                    </a>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          <span className="inline-block whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs border border-primary/40 text-primary bg-primary/10">
                            {ADMIN_TYPE_LABEL[a.admin_type] || a.admin_type || "—"}
                          </span>
                        </td>
                        <td className="p-3 num text-white/60 whitespace-nowrap">
                          {formatDT(a.created_at)}
                        </td>
                        <td className="p-3 num text-white/60 whitespace-nowrap">
                          {formatDT(a.last_login)}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <button
                              type="button"
                              onClick={() => handleEdit(a)}
                              disabled={a.is_super_admin && a.id !== user.id}
                              title="تعديل"
                              data-testid={`admin-team-edit-${a.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] border border-primary/40 text-primary/90 hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <PencilSimple size={12} /> تعديل
                            </button>
                            <button
                              type="button"
                              onClick={() => handleResetPassword(a)}
                              title="إعادة تعيين كلمة السر"
                              data-testid={`admin-team-reset-${a.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] border border-[#F57F17]/50 text-[#F57F17] hover:bg-[#F57F17]/10"
                            >
                              <Key size={12} /> كلمة السر
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(a)}
                              disabled={a.is_super_admin}
                              title={a.is_super_admin ? "لا يمكن حذف المدير العام" : "حذف"}
                              data-testid={`admin-team-delete-${a.id}`}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] border border-destructive/50 text-destructive hover:bg-destructive/10 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <Trash size={12} /> حذف
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      <AdminModal
        open={modalOpen}
        admin={editingAdmin}
        onClose={() => {
          setModalOpen(false);
          setEditingAdmin(null);
        }}
        onSaved={handleSaved}
      />
      <PasswordRevealModal
        password={revealPassword?.password}
        email={revealPassword?.email}
        onClose={() => setRevealPassword(null)}
      />
    </div>
  );
}
